
import { Vue, Options } from "vue-class-component";
import { Prop, Ref } from "vue-property-decorator";
import UIkit from "uikit";
import Page from "../molecules/Page.vue";
import Store from "../../store";
import HelpIcon from "../atoms/HelpIcon.vue";
import ActionButton from "../atoms/ActionButton.vue";
import { ClientApi, PersonalityDetailItem, UserStats, InstructionLevel } from "../../types";
import { StatsApi, StatsItemEnum, GetStat } from "../../autogen";
import { print } from "../../helpers/view-utils";
import LevelChart from "../atoms/LevelChart.vue";
import GaugeChart from "../atoms/GaugeChart.vue";
import StabilityGaugeChart from "../atoms/StabilityGaugeChart.vue";
import HumanSkillChart from "../atoms/HumanSkillChart.vue";
import Avatar from "../atoms/Avatar.vue";
import CulturalFitChart from "../molecules/CulturalFitChart.vue";
import BehaviorChart from "../atoms/BehaviorChart.vue";
import ProgressChart from "../molecules/ProgressChart.vue";
import TechnicalSkill from "../atoms/TechnicalSkill.vue";
import * as RoleHelper from "../../helpers/role-helper";
import { PersonalityDetailGroupTooltip, SocialityTooltip, ActivityMotivationDetailTooltip } from "../../helpers/const";

type PersonalityDetailGroupItems = { [key: string]: PersonalityDetailItem[] };
declare module "@vue/runtime-core" {
  export interface ComponentCustomProperties {
    $role: typeof RoleHelper;
  }
}

type ChartData = {
  examDate: string[];
  examDateOptions: Object;
  datas: Object;
};
type DataSet = {
  backgroundColor: string;
  borderColor: string;
  borderWidth: number;
  data: Object[];
  label: string;
};
type GraphData = {
  labels: string[] | string[][];
  datasets: DataSet[];
};

@Options({
  components: {
    Page,
    HelpIcon,
    ActionButton,
    LevelChart,
    GaugeChart,
    Avatar,
    CulturalFitChart,
    HumanSkillChart,
    BehaviorChart,
    ProgressChart,
    StabilityGaugeChart,
    TechnicalSkill
  }
})
export default class People extends Vue {
  globalErrors: { message: string }[] = [];
  name?: string = "";
  stats: GetStat | null = null;
  overallJudgementScores = ["A", "B", "C", "D", "E"];
  personalityDetailGroup: PersonalityDetailGroupItems = {};
  peopleId = "";
  changeable = true;
  showProgressComparisonButton = false;
  readonly progressChartId = "PROGRESS_COMPARISON_CHART";
  colors: string[] = ["rgba(255, 128, 140, 1)", "rgba(128, 140, 255, 1)"];
  progressChartData: ChartData | null = null;
  progressChartOptions = {
    interaction: {
      intersect: false,
      mode: "index"
    },
    scales: {
      y: {
        suggestedMin: 10,
        suggestedMax: 90
      }
    },
    plugins: {
      tooltip: {
        intersect: false,
        callbacks: {
          title: (tooltipItems: any) => {
            const title = tooltipItems[0].label.replaceAll(",", "").replaceAll("丨", "ー");
            return title;
          }
        }
      }
    }
  };
  lastBehaviorDisplay = false;
  lastBehaviorX = 0;
  lastBehaviorY = 0;
  isTisikiTop = false;
  isGinouTop = false;
  isSikouTop = false;
  isTaizinTop = false;
  isManagementTop = false;
  @Ref("modal") modal!: HTMLElement;
  @Prop({ type: Object }) printData?:
    | (GetStat & {
        name: string;
      })
    | null = null;
  @Prop({ type: Object }) options = {};
  @Prop({ type: Object }) statsItems? = [];
  uniqueTypes() {
    return Object.keys(this.personalityDetailGroup);
  }
  rowCount(type: string) {
    return this.personalityDetailGroup[type].length;
  }
  rowItems(type: string) {
    return this.personalityDetailGroup[type];
  }
  rowEvaluation(row: PersonalityDetailItem) {
    return row.value <= 20 ? "▲" : row.value >= 80 ? "●" : "";
  }
  async beforeMount() {
    if (this.printData) {
      this.stats = (this.printData as unknown) as UserStats;
      this.name = this.printData.name;
      this.changeable = false;
      let examDates = this.stats.ExamDate!.examDates!.map(date => this.toDateFormat(date)) || [];
      // 接し方座標 (前回位置)
      if (examDates.length >= 2) {
        this.lastBehaviorDisplay = true;
        let dataA = this.stats;           // 受験者のスタッツ全体
        let dataB = dataA.Progress;       // 受験者のスタッツから経年データを取得
        if (dataB !== undefined) {
          let dataC = dataB[1];           // 経年データの最新から1つ遡る(前回データ)
          if (dataC !== undefined) {
            let dataD = dataC.score;      // 前回データの各種スコア
            if (dataD !== undefined) {
              let zyuuzyunnSei = dataD[27].value;
              let zisyuSei = dataD[28].value;
              let tasseiYokkyuu = dataD[30].value;
              let ziritsuYokkyuu = dataD[31].value;
              let kyuutiYokkyuu = dataD[32].value;
              let kikiTaisei = dataD[33].value;
              let kinrouIyoku = dataD[34].value;
              this.lastBehaviorX = (Number(tasseiYokkyuu) + Number(ziritsuYokkyuu) + Number(kyuutiYokkyuu) + Number(kikiTaisei) + Number(kinrouIyoku)) / 5;
              this.lastBehaviorY = (Number(zisyuSei) + (100 - Number(zyuuzyunnSei))) / 2;
            }
          }
        }
      }
      // テクニカルスキル
      let baseStats = this.stats;
      let baseProgress = baseStats.Progress;
      if (baseProgress !== undefined) {
        let currentProgress = baseProgress[0];
        if (currentProgress !== undefined) {
          let baseScore = currentProgress.score;
          if (baseScore !== undefined) {
            // 必要な値を抽出
            let zizokusei = Number(baseScore[14].value);
            let kisokusei = Number(baseScore[15].value);
            let kyakkansei = Number(baseScore[11].value);
            let sekkyokusei = Number(baseScore[20].value);
            let zyuuzyunnsei = Number(baseScore[27].value);
            let zisyusei = Number(baseScore[28].value);
            let kyuutiYokkyuu = Number(baseScore[32].value);
            let yokubou = Number(baseScore[39].value);
            let sekininkan = Number(baseScore[22].value);
            let sidousei = Number(baseScore[24].value);
            let tasseiYokkyuu = Number(baseScore[30].value);
            let kyoutyousei = Number(baseScore[21].value);
            let tituzyo = Number(baseScore[38].value);
            let sinwa = Number(baseScore[37].value);
            let kyoukansei = Number(baseScore[25].value);
            let sihai = Number(baseScore[36].value);
            let antei = Number(baseScore[26].value);

            // テクニカルスキルの値を算出
            let tisiki = (zizokusei + kisokusei + sekkyokusei + zyuuzyunnsei + zisyusei + kyuutiYokkyuu + yokubou) / 7;
            let ginou = (sekkyokusei + zisyusei + sekininkan + sidousei + tasseiYokkyuu) / 5;
            let sikou = ((100 - kyuutiYokkyuu) + tasseiYokkyuu + kyoutyousei + tituzyo) / 4;
            let taizin =  ((100 - kisokusei) + kyakkansei + sekkyokusei + kyoutyousei + sinwa + kyoukansei + sihai) / 7;
            let management = ((100 - kisokusei) + kyakkansei + sekkyokusei + sidousei + kyoutyousei + sinwa + kyoukansei + antei) / 8;

            // 最高値のものを抽出
            let max = 0.0;
            max = tisiki;
            this.isTisikiTop = true;

            if (max == ginou) {
              this.isGinouTop = true;
            } else if (max < ginou) {
              this.isTisikiTop = false;
              this.isGinouTop = true;
              max = ginou;
            }

            if (max == sikou) {
              this.isSikouTop = true;
            } else if (max < sikou) {
              this.isTisikiTop = false;
              this.isGinouTop = false;
              this.isSikouTop = true;
              max = sikou;
            }

            if (max == taizin) {
              this.isTaizinTop = true;
            } else if (max < taizin) {
              this.isTisikiTop = false;
              this.isGinouTop = false;
              this.isSikouTop = false;
              this.isTaizinTop = true;
              max = taizin;
            }

            if (max == management) {
              this.isManagementTop = true;
            } else if (max < management) {
              this.isTisikiTop = false;
              this.isGinouTop = false;
              this.isSikouTop = false;
              this.isTaizinTop = false;
              this.isManagementTop= true;
              max = management;
            }
          }
        }
      }
    } else {
      const paths = this.$route.path.split("/");
      const peopleType = paths[paths.length - 2];
      const isEmployee = peopleType === "employees";
      const prefix = isEmployee ? "EMP" : "APL";
      const suffix = this.$route.params.suffix && this.$route.params.suffix.length ? this.$route.params.suffix : "";
      const rawPeopleId = this.$route.params["peopleId"] as string;
      // EMP#1#RetiredのようなSKに変換
      const peopleId = (this.peopleId = `${prefix}#${rawPeopleId}#${suffix}`);
      const response = await new StatsApi().getPeopleStats({
        peopleIds: [peopleId],
        statsItems: this.statsItems?.length
          ? this.statsItems
          : [
              StatsItemEnum.CulturalFit,
              StatsItemEnum.HumanSkill,
              StatsItemEnum.PersonalityDetail,
              StatsItemEnum.Sociality,
              StatsItemEnum.ActivityMotivationDetail,
              StatsItemEnum.Behavior,
              StatsItemEnum.SelfEvaluation,
              StatsItemEnum.SimilarType,
              StatsItemEnum.InterviewTips,
              StatsItemEnum.Reliability,
              StatsItemEnum.Judgement,
              StatsItemEnum.JobInterest,
              StatsItemEnum.LastExamDate,
              StatsItemEnum.ExamDate,
              StatsItemEnum.Progress
            ]
      });
      const data = response.data as UserStats;
      if (!Object.keys(data).length) {
        alert("未受検または受検結果処理中のためデータを表示できません");
        this.$router.go(-1);
        return;
      }
      this.stats = data[peopleId].stats;

      this.name = `${data[peopleId].profile.lastName} ${data[peopleId].profile.firstName}`;
      const behavior = this.stats["Behavior"]!;
      behavior.x = behavior.x > 70 ? 70 : behavior.x < 30 ? 30 : behavior.x;
      behavior.y = behavior.y > 70 ? 70 : behavior.y < 30 ? 30 : behavior.y;

      let examDates = this.stats.ExamDate!.examDates!.map(date => this.toDateFormat(date)) || [];
      this.showProgressComparisonButton = examDates.length >= 2;
      this.progressChartData = (examDates
        ? {
            examDate: examDates,
            examDateOptions: {
              mode: "tags",
              value: [],
              label: "label",
              trackBy: "value",
              closeOnSelect: false,
              options: examDates.map(date => ({ value: date, label: `${date} 時` })),
              reachable: true,
              createOption: false,
              delay: 0
            },
            datas: {
              labels: [],
              datasets: []
            }
          }
        : {}) as ChartData;

      const targetStats = (data as UserStats)![this.peopleId].stats["Progress"];
      if (targetStats && targetStats.length > 1) {
        const laterProgresses = targetStats.sort((a, b) => (a.date > b.date ? -1 : 1)).slice(0, 2);
        (this.progressChartData!.datas as GraphData).labels = laterProgresses[0].score.map(score =>
          score.name
            .replace(/ー/g, "丨")
            .replace("ﾓﾗﾄﾘｱﾑ", "モラトリアム")
            .split("")
        );
        (this.progressChartData!.datas as GraphData).datasets = laterProgresses.map((progress, index) => ({
          label: `${this.toDateFormat(progress.date)} 時`,
          data: progress.score.map(score => Math.round(score.value)),
          backgroundColor: this.colors[index],
          borderColor: this.colors[index],
          borderWidth: 2
        }));
      }
      // 接し方座標 (前回位置)
      if (examDates.length >= 2) {
        this.lastBehaviorDisplay = true;
        let dataA = this.stats;           // 受験者のスタッツ全体
        let dataB = dataA.Progress;       // 受験者のスタッツから経年データを取得
        if (dataB !== undefined) {
          let dataC = dataB[1];           // 経年データの最新から1つ遡る(前回データ)
          if (dataC !== undefined) {
            let dataD = dataC.score;      // 前回データの各種スコア
            if (dataD !== undefined) {
              let zyuuzyunnSei = dataD[27].value;
              let zisyuSei = dataD[28].value;
              let tasseiYokkyuu = dataD[30].value;
              let ziritsuYokkyuu = dataD[31].value;
              let kyuutiYokkyuu = dataD[32].value;
              let kikiTaisei = dataD[33].value;
              let kinrouIyoku = dataD[34].value;
              this.lastBehaviorX = (Number(tasseiYokkyuu) + Number(ziritsuYokkyuu) + Number(kyuutiYokkyuu) + Number(kikiTaisei) + Number(kinrouIyoku)) / 5;
              this.lastBehaviorY = (Number(zisyuSei) + (100 - Number(zyuuzyunnSei))) / 2;
            }
          }
        }
      }
      // テクニカルスキル
      let baseStats = this.stats;
      let baseProgress = baseStats.Progress;
      if (baseProgress !== undefined) {
        let currentProgress = baseProgress[0];
        if (currentProgress !== undefined) {
          let baseScore = currentProgress.score;
          if (baseScore !== undefined) {
            // 必要な値を抽出
            let zizokusei = Number(baseScore[14].value);
            let kisokusei = Number(baseScore[15].value);
            let kyakkansei = Number(baseScore[11].value);
            let sekkyokusei = Number(baseScore[20].value);
            let zyuuzyunnsei = Number(baseScore[27].value);
            let zisyusei = Number(baseScore[28].value);
            let kyuutiYokkyuu = Number(baseScore[32].value);
            let yokubou = Number(baseScore[39].value);
            let sekininkan = Number(baseScore[22].value);
            let sidousei = Number(baseScore[24].value);
            let tasseiYokkyuu = Number(baseScore[30].value);
            let kyoutyousei = Number(baseScore[21].value);
            let tituzyo = Number(baseScore[38].value);
            let sinwa = Number(baseScore[37].value);
            let kyoukansei = Number(baseScore[25].value);
            let sihai = Number(baseScore[36].value);
            let antei = Number(baseScore[26].value);

            // テクニカルスキルの値を算出
            let tisiki = (zizokusei + kisokusei + sekkyokusei + zyuuzyunnsei + zisyusei + kyuutiYokkyuu + yokubou) / 7;
            let ginou = (sekkyokusei + zisyusei + sekininkan + sidousei + tasseiYokkyuu) / 5;
            let sikou = ((100 - kyuutiYokkyuu) + tasseiYokkyuu + kyoutyousei + tituzyo) / 4;
            let taizin =  ((100 - kisokusei) + kyakkansei + sekkyokusei + kyoutyousei + sinwa + kyoukansei + sihai) / 7;
            let management = ((100 - kisokusei) + kyakkansei + sekkyokusei + sidousei + kyoutyousei + sinwa + kyoukansei + antei) / 8;

            // 最高値のものを抽出
            let max = 0.0;
            max = tisiki;
            this.isTisikiTop = true;

            if (max == ginou) {
              this.isGinouTop = true;
            } else if (max < ginou) {
              this.isTisikiTop = false;
              this.isGinouTop = true;
              max = ginou;
            }

            if (max == sikou) {
              this.isSikouTop = true;
            } else if (max < sikou) {
              this.isTisikiTop = false;
              this.isGinouTop = false;
              this.isSikouTop = true;
              max = sikou;
            }

            if (max == taizin) {
              this.isTaizinTop = true;
            } else if (max < taizin) {
              this.isTisikiTop = false;
              this.isGinouTop = false;
              this.isSikouTop = false;
              this.isTaizinTop = true;
              max = taizin;
            }

            if (max == management) {
              this.isManagementTop = true;
            } else if (max < management) {
              this.isTisikiTop = false;
              this.isGinouTop = false;
              this.isSikouTop = false;
              this.isTaizinTop = false;
              this.isManagementTop= true;
              max = management;
            }

          }
        }
      }

      try {
        const criteriaResponse = await new ClientApi().getHrCriteria({});
      } catch (err) {
        if (err.response.status === 403) {
          // skip this status
        } else {
          this.globalErrors = [{ message: err.message }];
        }
      }
    }
    this.personalityDetailGroup = this.stats!.PersonalityDetail!.items.reduce((a, b) => {
      if (!a[b.type]) a[b.type] = [];
      a[b.type].push(b);
      b.tooltip = PersonalityDetailGroupTooltip[b.aspect];
      return a;
    }, {} as PersonalityDetailGroupItems);
    if (this.stats) {
      if (this.stats.Sociality) {
        this.stats.Sociality = this.stats.Sociality.map(sociality => ({
          name: sociality.name,
          value: sociality.value,
          description: sociality.description,
          tooltip: SocialityTooltip[sociality.name as keyof typeof SocialityTooltip]
        }));
      }
      if (this.stats.ActivityMotivationDetail && this.stats.ActivityMotivationDetail.items) {
        this.stats.ActivityMotivationDetail.items = this.stats.ActivityMotivationDetail.items.map(detail => ({
          name: detail.name,
          value: detail.value,
          description: detail.description,
          tooltip: ActivityMotivationDetailTooltip[detail.name as keyof typeof ActivityMotivationDetailTooltip]
        }));
      }
    }
  }
  beforeUnmount() {
    if (this.modal) this.modal.remove();
  }
  async onProgressComparisonButtonClick() {
    UIkit.modal(this.modal).show();
  }
  async onWorksheetButtonClick() {
    Store.getInstance().setIsLoading(true);
    try {
      const response = await new ClientApi().getXlsPersonalWorksheet(
        { personId: this.peopleId },
        { responseType: "arraybuffer" }
      );
      const url = window.URL.createObjectURL(new Blob([response.data], { type: response.headers["content-type"] }));
      const link = document.createElement("a");
      link.href = url;
      // Filename format: [LastnameFirstName]_ワークシート_[datetime].xlsx
      const filename = `${this.name?.replace(/\s/g, "")}_ワークシート_${new Date().toLocaleString("jp-JP")}`;
      link.setAttribute("download", `${filename}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      Store.getInstance().setInstructionContent(InstructionLevel.Alert, "エラーが発生しました");
    } finally {
      Store.getInstance().setIsLoading(false);
    }
  }
  onPrintButtonClick() {
    print(StatsItemEnum.Judgement, Object.assign({ name: this.name }, this.stats));
  }
  toDateFormat(str: string) {
    return `${str.slice(0, 4)}/${str.slice(4, 6)}/${str.slice(6, 8)} ${str.slice(8, 10)}`.trim();
  }
}
